import { compactGeometry } from "@project/emerge/src/util/compactGeometry";
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";

/**
 * Radius of the rounding on a cut foam edge, in mm.
 *
 * A block of foam has no sharp edges: the cells at an edge have nothing to hold
 * them square, so a saw or hot wire leaves a soft corner. One millimetre is
 * enough to catch a highlight along every edge of a pad — which is the tell
 * that separates a soft block from a slab of grey plastic — and small enough
 * that the pad's stated size still reads off its silhouette.
 */
export const FOAM_EDGE_RADIUS_MM = 1;

/**
 * Name of the per-vertex occlusion attribute the baked foam material reads.
 *
 * Alongside it the geometry's `uv` attribute carries the pore coordinate in
 * millimetres, projected the same way `FoamMaterials` projects in the vertex
 * shader — see {@link solidProjectionUv}. Geometry that carries both can be
 * given the material variant that reads them instead of choosing a projection
 * per vertex, which matters wherever the vertex normals are not axis-aligned.
 */
export const FOAM_OCCLUSION_ATTRIBUTE = "foamOcclusion";

/**
 * Object-space planar projection of a point, down the dominant axis of its
 * normal, in millimetres.
 *
 * The CPU half of the projection `FoamMaterials` performs in its vertex shader;
 * the two must agree so that a pad wearing the baked variant of the material
 * shows the same slice of foam on its sides as a pad wearing the projected one.
 */
export function solidProjectionUv(
  normal: THREE.Vector3,
  position: THREE.Vector3,
): [number, number] {
  const ax = Math.abs(normal.x);
  const ay = Math.abs(normal.y);
  const az = Math.abs(normal.z);
  if (ay >= ax && ay >= az) return [position.x, position.z];
  if (ax >= az) return [position.z, position.y];
  return [position.x, position.y];
}

/**
 * Writes the projected millimetre `uv` and a unit occlusion onto every vertex.
 *
 * The occlusion is the neutral value; geometry that knows better (the spiked
 * pad) overwrites it for the vertices it has a figure for.
 */
export function attachFoamAttributes(geometry: THREE.BufferGeometry): void {
  const position = geometry.attributes.position as THREE.BufferAttribute;
  const normal = geometry.attributes.normal as THREE.BufferAttribute;
  const count = position.count;
  const uv = new Float32Array(count * 2);
  const occlusion = new Float32Array(count).fill(1);

  const p = new THREE.Vector3();
  const n = new THREE.Vector3();
  for (let i = 0; i < count; i++) {
    p.fromBufferAttribute(position, i);
    n.fromBufferAttribute(normal, i);
    const [u, v] = solidProjectionUv(n, p);
    uv[i * 2] = u;
    uv[i * 2 + 1] = v;
  }

  geometry.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
  geometry.setAttribute(
    FOAM_OCCLUSION_ATTRIBUTE,
    new THREE.BufferAttribute(occlusion, 1),
  );
}

/**
 * Ein Polster ohne Ausschnitte: ein Block mit weich gerundeten Kanten.
 *
 * Two segments to a rounding is a 300-triangle box, against the 12 of the
 * sharp one — nothing, against what a single rivet costs.
 *
 * @param width Pad width in mm, along x.
 * @param thickness Pad thickness in mm, along y.
 * @param depth Pad depth in mm, along z.
 */
export function createFoamBlockGeometry(
  width: number,
  thickness: number,
  depth: number,
): THREE.BufferGeometry {
  // A pad thinner than four radii would be all rounding and no face.
  const radius = Math.min(
    FOAM_EDGE_RADIUS_MM,
    width / 4,
    thickness / 4,
    depth / 4,
  );
  const geometry = new RoundedBoxGeometry(width, thickness, depth, 2, radius);
  attachFoamAttributes(geometry);
  return compactGeometry(geometry);
}

/**
 * Kantenlinien für einen gerundeten Block.
 *
 * Every dihedral on the rounding is below the edge threshold, so outlining the
 * mesh itself would draw nothing. The sharp box the pad was specified as is
 * what the drawing wants.
 */
export function createFoamBlockOutline(
  width: number,
  thickness: number,
  depth: number,
): THREE.BufferGeometry {
  return new THREE.BoxGeometry(width, thickness, depth);
}
